import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { useWebSocket } from '../hooks/useWebSocket';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Slider } from '../components/ui/slider';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '../components/ui/alert-dialog';
import { toast } from 'sonner';
import { 
  ArrowLeft, 
  Power, 
  Gauge, 
  Clock, 
  Plus,
  Trash2,
  Wifi,
  WifiOff,
  MapPin,
  Calendar,
  Settings,
  Droplets,
  Timer,
  Battery,
  RefreshCw
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Valve percentage presets
const PERCENTAGE_PRESETS = [10, 25, 50, 75, 100];

// Duration presets in minutes
const DURATION_PRESETS = [
  { value: 10, label: '10 min' },
  { value: 15, label: '15 min' },
  { value: 20, label: '20 min' },
  { value: 30, label: '30 min' },
  { value: 'custom', label: 'Custom / 自訂' }
];

const DeviceControlPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { t } = useLanguage();
  const { token } = useAuth();
  
  const [device, setDevice] = useState(null);
  const [schedules, setSchedules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [commanding, setCommanding] = useState(false);
  const [targetPressure, setTargetPressure] = useState(2.5);
  const [showAddSchedule, setShowAddSchedule] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  
  // Valve percentage state
  const [selectedPercentage, setSelectedPercentage] = useState(100);
  
  // Time control state
  const [selectedDuration, setSelectedDuration] = useState(10);
  const [customDuration, setCustomDuration] = useState(10);
  const [isCustomDuration, setIsCustomDuration] = useState(false);
  const [timeControlActive, setTimeControlActive] = useState(false);
  
  const [newSchedule, setNewSchedule] = useState({
    start_time: '06:00',
    duration_minutes: 30,
    repeat_type: 'daily'
  });

  const handleWebSocketMessage = useCallback((message) => {
    if (message.type === 'device_update' && message.device.id === id) {
      setDevice(message.device);
    }
  }, [id]);

  const { isConnected } = useWebSocket(token, handleWebSocketMessage);

  const fetchDevice = async () => {
    try {
      const [deviceRes, schedulesRes] = await Promise.all([
        axios.get(`${API}/devices/${id}`),
        axios.get(`${API}/schedules?device_id=${id}`)
      ]);
      setDevice(deviceRes.data);
      setSchedules(schedulesRes.data);
      setTargetPressure(deviceRes.data.target_pressure);
    } catch (error) {
      console.error('Failed to fetch device:', error);
      toast.error('Failed to load device');
      navigate('/app');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDevice();
  }, [id]);

  const sendCommand = async (command, value = null) => {
    if (device?.is_locked) {
      toast.error('Device is locked / 設備已鎖定');
      return;
    }
    
    setCommanding(true);
    try {
      const response = await axios.post(`${API}/devices/${id}/command`, { command, value });
      setDevice(response.data);
      toast.success(`Command sent: ${command} / 指令已發送`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Command failed');
    } finally {
      setCommanding(false);
    }
  };

  // FAC Cloud control - Open/Close valve with percentage
  const handleFacValveControl = async (action, percentage = 100) => {
    if (device?.is_locked) {
      toast.error('Device is locked / 設備已鎖定');
      return;
    }
    
    setCommanding(true);
    try {
      // Use device serial_number as FAC device ID
      const facDeviceId = device.serial_number || id;
      const response = await axios.post(
        `${API}/fac/devices/${facDeviceId}/control/single?action=${action}&percentage=${percentage}`
      );
      
      if (response.data.success) {
        const taskCode = response.data.task_code;
        toast.success(
          action === 'open' 
            ? `Valve opened at ${percentage}% / 閥門已開啟 ${percentage}% (Task: ${taskCode})`
            : `Valve closed / 閥門已關閉 (Task: ${taskCode})`
        );
        // Update local device state
        setDevice(prev => ({ ...prev, valve_state: action === 'open' ? 'open' : 'closed' }));
      } else {
        toast.warning('Command sent but no confirmation / 指令已發送但未確認');
      }
    } catch (error) {
      const errorMsg = error.response?.data?.detail || 'Control failed / 控制失敗';
      toast.error(errorMsg);
      // If FAC API fails, try local command as fallback
      if (error.response?.status === 404) {
        sendCommand(action);
      }
    } finally {
      setCommanding(false);
    }
  };

  // FAC Cloud control - Time-based irrigation
  const handleFacTimeControl = async () => {
    if (device?.is_locked) {
      toast.error('Device is locked / 設備已鎖定');
      return;
    }
    
    const duration = isCustomDuration ? customDuration : selectedDuration;
    if (duration < 1 || duration > 1440) {
      toast.error('Duration must be 1-1440 minutes / 持續時間需為1-1440分鐘');
      return;
    }
    
    setTimeControlActive(true);
    setCommanding(true);
    try {
      const facDeviceId = device.serial_number || id;
      const response = await axios.post(
        `${API}/fac/devices/${facDeviceId}/control/time?duration_minutes=${duration}&percentage=${selectedPercentage}`
      );
      
      if (response.data.success) {
        toast.success(`Irrigation started: ${duration} min at ${selectedPercentage}% / 灌溉已啟動：${duration}分鐘，${selectedPercentage}%開度`);
        setDevice(prev => ({ ...prev, valve_state: 'open' }));
      }
    } catch (error) {
      const errorMsg = error.response?.data?.detail || 'Time control failed';
      toast.error(errorMsg);
      setTimeControlActive(false);
    } finally {
      setCommanding(false);
    }
  };

  // FAC Cloud control - Set pressure
  const handleFacPressureSet = async () => {
    if (device?.is_locked) {
      toast.error('Device is locked / 設備已鎖定');
      return;
    }
    
    setCommanding(true);
    try {
      const facDeviceId = device.serial_number || id;
      const response = await axios.post(
        `${API}/fac/devices/${facDeviceId}/pressure?pressure=${targetPressure}`
      );
      
      if (response.data.success) {
        toast.success(`Pressure set to ${targetPressure} bar / 壓力已設定為 ${targetPressure} bar`);
        setDevice(prev => ({ ...prev, target_pressure: targetPressure }));
      }
    } catch (error) {
      const errorMsg = error.response?.data?.detail || 'Pressure setting failed';
      toast.error(errorMsg);
      // Fallback to local command
      if (error.response?.status === 404) {
        sendCommand('set_pressure', targetPressure);
      }
    } finally {
      setCommanding(false);
    }
  };

  const handleValveToggle = () => {
    const action = device.valve_state === 'open' ? 'close' : 'open';
    // Use FAC Cloud control if device has a FAC serial number (IMEI starting with 8624990)
    const isFacDevice = device.serial_number && device.serial_number.startsWith('8624990');
    if (isFacDevice || device.fac_bound) {
      handleFacValveControl(action, action === 'open' ? selectedPercentage : 0);
    } else {
      sendCommand(action);
    }
  };

  const handlePressureChange = (value) => {
    setTargetPressure(value[0]);
  };

  const handlePressureSet = () => {
    sendCommand('set_pressure', targetPressure);
  };

  const handleAddSchedule = async (e) => {
    e.preventDefault();
    try {
      const response = await axios.post(`${API}/schedules`, {
        device_id: id,
        ...newSchedule
      });
      setSchedules([...schedules, response.data]);
      toast.success('Schedule added / 排程已新增');
      setShowAddSchedule(false);
      setNewSchedule({ start_time: '06:00', duration_minutes: 30, repeat_type: 'daily' });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add schedule');
    }
  };

  const handleDeleteSchedule = async (scheduleId) => {
    try {
      await axios.delete(`${API}/schedules/${scheduleId}`);
      setSchedules(schedules.filter(s => s.id !== scheduleId));
      toast.success('Schedule deleted / 排程已刪除');
    } catch (error) {
      toast.error('Failed to delete schedule');
    }
  };

  const handleDeleteDevice = async () => {
    try {
      await axios.delete(`${API}/devices/${id}`);
      toast.success('Device deleted / 設備已刪除');
      navigate('/app');
    } catch (error) {
      toast.error('Failed to delete device');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-fac-dark pt-16 flex items-center justify-center">
        <div className="text-fac-green">{t('loading')}</div>
      </div>
    );
  }

  if (!device) return null;

  const isOnline = device.status === 'online';
  const isOpen = device.valve_state === 'open';

  return (
    <div className="min-h-screen bg-fac-dark pt-16 pb-24 md:pb-8">
      <div className="max-w-4xl mx-auto px-4 md:px-8 py-6">
        {/* Header */}
        <div className="flex items-center gap-4 mb-6">
          <Button
            variant="ghost"
            onClick={() => navigate('/app')}
            className="text-white hover:text-fac-green"
            data-testid="back-btn"
          >
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div className="flex-1">
            <h1 className="font-heading font-bold text-2xl text-white" data-testid="device-name">
              {device.name}
            </h1>
            <p className="text-muted-foreground font-mono text-sm">{device.serial_number}</p>
          </div>
          <div className={`flex items-center gap-2 ${isConnected ? 'text-fac-green' : 'text-fac-red'}`}>
            {isConnected ? <Wifi className="w-5 h-5" /> : <WifiOff className="w-5 h-5" />}
          </div>
        </div>

        {/* Status Card */}
        <Card className="bg-fac-card border-border mb-6">
          <CardContent className="p-6">
            <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
              <div className="text-center">
                <p className="text-sm text-muted-foreground font-chinese mb-1">Status / 狀態</p>
                <p className={`font-bold text-lg ${isOnline ? 'text-fac-green' : 'text-fac-red'}`}>
                  {isOnline ? t('device_online') : t('device_offline')}
                </p>
              </div>
              <div className="text-center">
                <p className="text-sm text-muted-foreground font-chinese mb-1">Valve / 閥門</p>
                <p className={`font-bold text-lg ${isOpen ? 'text-fac-green' : 'text-white'}`}>
                  {isOpen ? 'OPEN / 開啟' : 'CLOSED / 關閉'}
                </p>
              </div>
              <div className="text-center">
                <p className="text-sm text-muted-foreground font-chinese mb-1">Pressure / 水壓</p>
                <p className="font-bold text-lg text-fac-blue">{device.pressure} bar</p>
              </div>
              <div className="text-center">
                <p className="text-sm text-muted-foreground font-chinese mb-1">Target / 目標</p>
                <p className="font-bold text-lg text-white">{device.target_pressure} bar</p>
              </div>
              {device.battery !== undefined && (
                <div className="text-center">
                  <p className="text-sm text-muted-foreground font-chinese mb-1">Battery / 電量</p>
                  <p className={`font-bold text-lg flex items-center justify-center gap-1 ${
                    device.battery < 20 ? 'text-fac-red' : device.battery < 50 ? 'text-fac-yellow' : 'text-fac-green'
                  }`}>
                    <Battery className="w-4 h-4" />
                    {device.battery}%
                  </p>
                </div>
              )}
            </div>
            
            {device.location && (
              <div className="flex items-center gap-2 mt-4 pt-4 border-t border-border text-muted-foreground">
                <MapPin className="w-4 h-4" />
                <span className="font-chinese">{device.location}</span>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Valve Control with Percentage */}
        <Card className="bg-fac-card border-border mb-6" data-testid="valve-control-card">
          <CardHeader>
            <CardTitle className="font-heading text-white uppercase flex items-center gap-2">
              <Power className="w-5 h-5 text-fac-green" />
              {t('control_valve')}
              <span className="text-fac-green font-chinese text-base ml-2">閥門控制</span>
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Percentage Selection */}
            <div>
              <Label className="text-white font-chinese mb-2 block">
                <Droplets className="w-4 h-4 inline mr-2 text-fac-blue" />
                Valve Opening / 開度選擇
              </Label>
              <div className="grid grid-cols-5 gap-2" data-testid="percentage-buttons">
                {PERCENTAGE_PRESETS.map((pct) => (
                  <Button
                    key={pct}
                    variant={selectedPercentage === pct ? "default" : "outline"}
                    onClick={() => setSelectedPercentage(pct)}
                    className={`font-bold transition-all ${
                      selectedPercentage === pct 
                        ? 'bg-fac-green text-black border-fac-green' 
                        : 'border-fac-green/50 text-fac-green hover:bg-fac-green/10'
                    }`}
                    data-testid={`percentage-${pct}`}
                  >
                    {pct}%
                  </Button>
                ))}
              </div>
              <p className="text-sm text-muted-foreground mt-2 font-chinese">
                Selected: {selectedPercentage}% opening / 已選擇：{selectedPercentage}% 開度
              </p>
            </div>

            {/* Valve Toggle Button */}
            <Button
              onClick={handleValveToggle}
              disabled={commanding || device.is_locked}
              className={`w-full h-20 text-xl font-bold uppercase transition-all ${
                isOpen 
                  ? 'bg-fac-red hover:bg-fac-red/80 text-white glow-red'
                  : 'bg-fac-green hover:bg-fac-green-dim text-black glow-green'
              }`}
              data-testid="valve-toggle-btn"
            >
              {commanding ? (
                <RefreshCw className="w-6 h-6 animate-spin" />
              ) : (
                <>
                  <Power className="w-6 h-6 mr-2" />
                  {isOpen ? t('control_close') : `${t('control_open')} ${selectedPercentage}%`}
                </>
              )}
            </Button>
            <p className="text-center text-sm text-muted-foreground font-chinese">
              {isOpen 
                ? 'Tap to close valve / 點擊關閉閥門' 
                : `Tap to open valve at ${selectedPercentage}% / 點擊開啟閥門 ${selectedPercentage}%`
              }
            </p>
          </CardContent>
        </Card>

        {/* Time-Based Irrigation Control */}
        <Card className="bg-fac-card border-border mb-6" data-testid="time-control-card">
          <CardHeader>
            <CardTitle className="font-heading text-white uppercase flex items-center gap-2">
              <Timer className="w-5 h-5 text-fac-yellow" />
              Timed Irrigation
              <span className="text-fac-green font-chinese text-base ml-2">定時灌溉</span>
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Duration Selection */}
            <div>
              <Label className="text-white font-chinese mb-2 block">
                <Clock className="w-4 h-4 inline mr-2 text-fac-yellow" />
                Duration / 持續時間
              </Label>
              <div className="grid grid-cols-3 md:grid-cols-5 gap-2" data-testid="duration-buttons">
                {DURATION_PRESETS.map((preset) => (
                  <Button
                    key={preset.value}
                    variant={(preset.value === 'custom' ? isCustomDuration : selectedDuration === preset.value && !isCustomDuration) ? "default" : "outline"}
                    onClick={() => {
                      if (preset.value === 'custom') {
                        setIsCustomDuration(true);
                      } else {
                        setIsCustomDuration(false);
                        setSelectedDuration(preset.value);
                      }
                    }}
                    className={`font-bold transition-all text-sm ${
                      (preset.value === 'custom' ? isCustomDuration : selectedDuration === preset.value && !isCustomDuration)
                        ? 'bg-fac-yellow text-black border-fac-yellow' 
                        : 'border-fac-yellow/50 text-fac-yellow hover:bg-fac-yellow/10'
                    }`}
                    data-testid={`duration-${preset.value}`}
                  >
                    {preset.label}
                  </Button>
                ))}
              </div>
            </div>

            {/* Custom Duration Input */}
            {isCustomDuration && (
              <div className="flex items-center gap-3">
                <Input
                  type="number"
                  min={1}
                  max={1440}
                  value={customDuration}
                  onChange={(e) => setCustomDuration(parseInt(e.target.value) || 1)}
                  className="bg-fac-dark border-fac-yellow/50 text-white w-24"
                  data-testid="custom-duration-input"
                />
                <span className="text-white font-chinese">minutes / 分鐘</span>
              </div>
            )}

            {/* Time Control Summary */}
            <div className="p-3 bg-fac-paper rounded-lg border border-border">
              <p className="text-white font-chinese text-sm">
                <strong>Summary / 摘要:</strong> Open valve at <span className="text-fac-green font-bold">{selectedPercentage}%</span> for <span className="text-fac-yellow font-bold">{isCustomDuration ? customDuration : selectedDuration} minutes</span>
              </p>
              <p className="text-muted-foreground text-xs mt-1">
                開啟閥門 {selectedPercentage}%，持續 {isCustomDuration ? customDuration : selectedDuration} 分鐘
              </p>
            </div>

            {/* Start Irrigation Button */}
            <Button
              onClick={handleFacTimeControl}
              disabled={commanding || device.is_locked || timeControlActive}
              className="w-full h-14 text-lg font-bold bg-fac-yellow text-black hover:bg-fac-yellow/90"
              data-testid="start-irrigation-btn"
            >
              {commanding ? (
                <RefreshCw className="w-5 h-5 animate-spin mr-2" />
              ) : (
                <Timer className="w-5 h-5 mr-2" />
              )}
              {timeControlActive ? 'Irrigation Running... / 灌溉進行中...' : 'START TIMED IRRIGATION / 開始定時灌溉'}
            </Button>
          </CardContent>
        </Card>

        {/* Pressure Control */}
        <Card className="bg-fac-card border-border mb-6" data-testid="pressure-control-card">
          <CardHeader>
            <CardTitle className="font-heading text-white uppercase flex items-center gap-2">
              <Gauge className="w-5 h-5 text-fac-blue" />
              {t('control_pressure')}
              <span className="text-fac-green font-chinese text-base ml-2">壓力設定</span>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="mb-6">
              <div className="flex justify-between mb-2">
                <span className="text-muted-foreground">0.5 bar</span>
                <span className="text-2xl font-bold text-fac-blue">{targetPressure.toFixed(1)} bar</span>
                <span className="text-muted-foreground">5.0 bar</span>
              </div>
              <Slider
                value={[targetPressure]}
                onValueChange={handlePressureChange}
                min={0.5}
                max={5}
                step={0.1}
                className="mb-4"
                data-testid="pressure-slider"
              />
            </div>
            <Button
              onClick={handleFacPressureSet}
              disabled={commanding || device.is_locked || targetPressure === device.target_pressure}
              className="w-full bg-fac-blue text-black font-bold"
              data-testid="set-pressure-btn"
            >
              {commanding ? <RefreshCw className="w-4 h-4 animate-spin mr-2" /> : <Gauge className="w-4 h-4 mr-2" />}
              SET PRESSURE / 設定壓力
            </Button>
          </CardContent>
        </Card>

        {/* Schedules */}
        <Card className="bg-fac-card border-border mb-6" data-testid="schedules-card">
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="font-heading text-white uppercase flex items-center gap-2">
              <Calendar className="w-5 h-5 text-fac-yellow" />
              {t('control_schedule')}
              <span className="text-fac-green font-chinese text-base ml-2">灌溉排程</span>
            </CardTitle>
            <Button
              size="sm"
              onClick={() => setShowAddSchedule(true)}
              className="bg-fac-green text-black font-bold"
              data-testid="add-schedule-btn"
            >
              <Plus className="w-4 h-4 mr-1" />
              {t('control_add_schedule')}
            </Button>
          </CardHeader>
          <CardContent>
            {schedules.length === 0 ? (
              <p className="text-center text-muted-foreground py-4 font-chinese">
                No schedules yet / 尚無排程
              </p>
            ) : (
              <div className="space-y-3">
                {schedules.map((schedule) => (
                  <div
                    key={schedule.id}
                    className="flex items-center justify-between p-4 bg-fac-paper rounded-lg border border-border"
                    data-testid={`schedule-${schedule.id}`}
                  >
                    <div className="flex items-center gap-4">
                      <Clock className="w-5 h-5 text-fac-yellow" />
                      <div>
                        <p className="text-white font-bold">{schedule.start_time}</p>
                        <p className="text-sm text-muted-foreground font-chinese">
                          {schedule.duration_minutes} {t('minutes')} • {schedule.repeat_type === 'daily' ? t('control_daily') : schedule.repeat_type === 'weekly' ? t('control_weekly') : t('control_once')}
                        </p>
                      </div>
                    </div>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => handleDeleteSchedule(schedule.id)}
                      className="text-fac-red hover:bg-fac-red/10"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Device Settings */}
        <Card className="bg-fac-card border-border">
          <CardHeader>
            <CardTitle className="font-heading text-white uppercase flex items-center gap-2">
              <Settings className="w-5 h-5" />
              Settings / 設定
            </CardTitle>
          </CardHeader>
          <CardContent>
            <Button
              variant="destructive"
              onClick={() => setShowDeleteConfirm(true)}
              className="w-full"
              data-testid="delete-device-btn"
            >
              <Trash2 className="w-4 h-4 mr-2" />
              Delete Device / 刪除設備
            </Button>
          </CardContent>
        </Card>
      </div>

      {/* Add Schedule Dialog */}
      <Dialog open={showAddSchedule} onOpenChange={setShowAddSchedule}>
        <DialogContent className="bg-fac-card border-border max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading text-white uppercase">
              {t('control_add_schedule')}
              <span className="block text-fac-green font-chinese text-base mt-1">新增排程</span>
            </DialogTitle>
          </DialogHeader>
          
          <form onSubmit={handleAddSchedule} className="space-y-4" data-testid="add-schedule-form">
            <div>
              <Label className="text-white font-chinese">{t('control_start_time')} / 開始時間</Label>
              <Input
                type="time"
                required
                value={newSchedule.start_time}
                onChange={(e) => setNewSchedule({ ...newSchedule, start_time: e.target.value })}
                className="mt-2 bg-fac-dark border-border focus:border-fac-green text-white"
                data-testid="schedule-time-input"
              />
            </div>
            
            <div>
              <Label className="text-white font-chinese">{t('control_duration')} / 持續時間（分鐘）</Label>
              <Input
                type="number"
                required
                min={1}
                max={480}
                value={newSchedule.duration_minutes}
                onChange={(e) => setNewSchedule({ ...newSchedule, duration_minutes: parseInt(e.target.value) })}
                className="mt-2 bg-fac-dark border-border focus:border-fac-green text-white"
                data-testid="schedule-duration-input"
              />
            </div>
            
            <div>
              <Label className="text-white font-chinese">{t('control_repeat')} / 重複</Label>
              <Select
                value={newSchedule.repeat_type}
                onValueChange={(value) => setNewSchedule({ ...newSchedule, repeat_type: value })}
              >
                <SelectTrigger className="mt-2 bg-fac-dark border-border text-white" data-testid="schedule-repeat-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-fac-card border-border">
                  <SelectItem value="once">{t('control_once')} / 單次</SelectItem>
                  <SelectItem value="daily">{t('control_daily')} / 每日</SelectItem>
                  <SelectItem value="weekly">{t('control_weekly')} / 每週</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <DialogFooter className="gap-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => setShowAddSchedule(false)}
                className="border-border text-white"
              >
                {t('cancel')}
              </Button>
              <Button
                type="submit"
                className="bg-fac-green text-black font-bold"
                data-testid="schedule-submit-btn"
              >
                {t('save')}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={showDeleteConfirm} onOpenChange={setShowDeleteConfirm}>
        <AlertDialogContent className="bg-fac-card border-border">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-white">Delete Device? / 刪除設備？</AlertDialogTitle>
            <AlertDialogDescription className="font-chinese">
              This action cannot be undone. The device will be removed from your account.
              <br />
              此操作無法撤銷。設備將從您的帳戶中移除。
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="border-border text-white">{t('cancel')}</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteDevice}
              className="bg-fac-red text-white"
              data-testid="confirm-delete-btn"
            >
              {t('delete')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default DeviceControlPage;
